import { Property } from "@greystar/lib";
import { AvailableUnitExtended } from "../../types/property-types";

type AnalyticsData = {
  [key: string]: any;
};

export interface FeeState {
  selectedUnit: AvailableUnitExtended | null;
  selectedMoveInDate: string;
  selectedLeaseTerm: string;
  numberOfApplicants: number;
  allFees: Array<{
    id: string | number;
    activeCount?: number;
    [key: string]: any;
  }>;
}

function normalizeLabelToKey(label: string): string {
  return label
    .replace(/[^\w\s\/]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[\s\/]+/g, "_");
}

type Fee = {
  id: string | number;
  label: string;
};

export const feesIdMapper: Record<string, string> = {};

export const setFeesIdMapper = (allFees: Fee[]) => {
  Object.keys(feesIdMapper).forEach((key) => delete feesIdMapper[key]);

  allFees.forEach((f) => {
    const normalizedKey = normalizeLabelToKey(f.label);
    feesIdMapper[normalizedKey] = String(f.id);
  });

  return feesIdMapper;
};

const getActiveCountById = (
  fees: FeeState["allFees"],
  id: string,
): number | "" => {
  const match = fees?.find((fee: any) => String(fee.id) === id);
  return match?.activeCount ?? 0;
};

export function getFeeKeyById(
  feeId: string | number,
): string | number | undefined {
  return Object.entries(feesIdMapper).find(([, value]) => value === feeId)?.[0];
}

export function getFeesAnalyticsData(
  property: Property,
  feeState: FeeState,
  activeStep: number,
): AnalyticsData {
  const selectedUnit = feeState.selectedUnit;

  const location = property?.location ?? {};
  const addressParts = [
    location?.address,
    location?.city,
    location?.state,
    location?.postalCode,
  ].filter((part) => part?.trim());
  const property_location =
    addressParts.length > 0 ? addressParts.join(", ") : "";

  const dynamicFeeData = Object.entries(feesIdMapper).reduce(
    (acc, [key, feeId]) => {
      acc[key] = getActiveCountById(feeState.allFees, feeId);
      return acc;
    },
    {} as AnalyticsData,
  );

  return {
    state: property?.location?.state ?? "",
    property_location,
    property_name: property?.name ?? "",
    property_id: property?.id ?? "",
    floor_plan_name: selectedUnit?.floorPlanLabel ?? "",
    floor_plan_id: selectedUnit?.floorPlanId ?? "",
    unit_no: selectedUnit?.unitNumber ?? "",
    module_name: "fee transparency calculator",
    total_monthly_price_min: selectedUnit?.minPrice ?? "",
    total_monthly_price_max: selectedUnit?.maxBasePrice ?? "",
    move_in_date: feeState?.selectedMoveInDate ?? "",
    lease_term: feeState?.selectedLeaseTerm ?? "",
    number_of_applicants: feeState.numberOfApplicants ?? "",
    click_text: "",
    click_type: "internal",
    destination_url: "/",
    current_page: activeStep === 1 ? "fee" : "costBreakdown",
    ...dynamicFeeData,
  };
}
